import { AlertTriangle, Copy } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import useAlert from 'src/hooks/useAlert';

import { PRIORITY_BADGE_COPY } from '../../constants';
import {
  copyTextToClipboard,
  formatDue,
  getPatient,
  getTaskDeepLinkUrl,
  isAssessmentTask,
  isLinkedTask,
  isMessageTask,
} from '../../helpers';
import { Badge } from '../Badge';
import styles from './TaskRow.module.scss';

const TaskRow = ({ task, compact, onSelect, selected, onAssignToMe, isAssigning }) => {
  const { showAlert, AlertType } = useAlert();

  if (!task) return null;

  const patient = task?.patient ?? getPatient(task?.patientId);
  const due = task?.due ? formatDue(task.due) : { text: '—', urgent: false };
  const isBlocked = task.status === 'blocked';
  const isFlagged = task.status === 'flagged';
  const isCompleted = task.status === 'done';
  const isBlockedOrFlagged = task.status === 'blocked' || task.status === 'flagged';
  const isLinked = isLinkedTask(task);
  const taskPriority = task?.priority ?? 'low';
  const ownerLabel = `${task?.owner || ''}`.trim();
  const isUnassigned = !ownerLabel || ownerLabel.toLowerCase() === 'unassigned';
  const taskIsMessage = isMessageTask(task);
  const taskIsAssessment = isAssessmentTask(task);
  const showPriorityBorder = !isCompleted;
  const priorityBorderClass = isLinked
    ? styles.rowPriorityLinked
    : styles[`rowPriority${taskPriority}`] || '';
  const showCriticalIcon =
    !isLinked &&
    !isBlocked &&
    !isFlagged &&
    showPriorityBorder &&
    taskPriority === 'critical' &&
    !taskIsMessage &&
    !taskIsAssessment;
  const taskDeepLinkUrl = getTaskDeepLinkUrl(task);

  const handleCopyTaskLink = async (event) => {
    event.stopPropagation();
    if (!taskDeepLinkUrl) return;

    try {
      const didCopy = await copyTextToClipboard(taskDeepLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Task link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy task link.');
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(task)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.(task);
        }
      }}
      className={[
        styles.row,
        compact ? styles.compact : '',
        selected ? styles.selected : '',
        !compact && showPriorityBorder ? priorityBorderClass : '',
      ].join(' ')}
    >
      {taskIsMessage ? (
        <span className={styles.messageIcon} aria-hidden="true" />
      ) : taskIsAssessment ? (
        <span className={styles.assessmentIcon} aria-hidden="true" />
      ) : showCriticalIcon ? (
        <span className={styles.criticalIcon} aria-hidden="true">
          <AlertTriangle className={styles.criticalIconGlyph} strokeWidth={2.2} />
        </span>
      ) : (
        <div
          className={[
            styles.checkBox,
            taskPriority === 'critical' ? styles.checkCritical : '',
            isBlockedOrFlagged ? styles.checkFlagged : '',
          ].join(' ')}
        >
          {task.status === 'done' && '✓'}
        </div>
      )}
      <div className={styles.main}>
        <div className={styles.titleRow}>
          <span className={styles.title}>{task.title}</span>
          {task.mustHave2026 && (
            <Badge variant="edge" className={styles.edgeBadge}>
              EDGE
            </Badge>
          )}
        </div>
        {task.caption && (
          <div className={`${styles.caption} ${compact ? styles.captionCompact : ''}`}>
            {task.caption}
          </div>
        )}
        {!compact && (
          <div className={styles.metaRow}>
            {patient?.name} → {task.owner}
          </div>
        )}
      </div>
      {!compact && (
        <>
          <div className={styles.assignCell}>
            {isUnassigned ? (
              <button
                type="button"
                className={styles.assignButton}
                disabled={isAssigning}
                onClick={(event) => {
                  event.stopPropagation();
                  onAssignToMe?.(task.id);
                }}
              >
                {isAssigning ? 'Assigning...' : 'Assign to me'}
              </button>
            ) : null}
          </div>
          <div className={styles.statusCell}>
            {task.status === 'blocked' ? (
              <Badge variant="critical">Blocked</Badge>
            ) : task.status === 'flagged' ? (
              <Badge variant="flagged">Flagged</Badge>
            ) : (
              <Badge>{task.status}</Badge>
            )}
          </div>
          <div className={styles.priorityCell}>
            <span
              className={`${styles.priorityBadge} ${styles[`priorityBadge${taskPriority}`] || ''}`}
            >
              {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
            </span>
            <button
              type="button"
              className={styles.copyButton}
              onClick={handleCopyTaskLink}
              disabled={!taskDeepLinkUrl}
              aria-label="Copy task link"
              title="Copy task link"
            >
              <Copy className={styles.copyIcon} strokeWidth={1.8} />
            </button>
          </div>
        </>
      )}
      <span className={`${styles.dueText} ${due.urgent ? styles.dueUrgent : ''}`}>{due.text}</span>
    </div>
  );
};

TaskRow.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    due: PropTypes.string,
    priority: PropTypes.string,
    status: PropTypes.string,
    team: PropTypes.string,
    owner: PropTypes.string,
    typeId: PropTypes.number,
    reasonId: PropTypes.number,
    title: PropTypes.string,
    mustHave2026: PropTypes.bool,
    caption: PropTypes.string,
    isVisible: PropTypes.bool,
  }),
  compact: PropTypes.bool,
  onSelect: PropTypes.func,
  selected: PropTypes.bool,
  onAssignToMe: PropTypes.func,
  isAssigning: PropTypes.bool,
};

export default TaskRow;
